import React, { useCallback, useContext, useEffect } from 'react';

import { SelectContext } from './context/SelectProvider';
import { getFixedOptions, getSelectedValues } from '../helpers';

import { Popover, PopoverTriggerProps } from '../Popover';
import { SelectOption } from '../types';

import useKeyNavigation from './hooks/useKeyNavigation';

export type SelectTriggerProps<T extends SelectOption> = Pick<
  PopoverTriggerProps<T>,
  'outline' | 'children' | 'label' | 'disabled' | 'autoFocus' | 'triggerId'
>;

const SelectTrigger = <T extends SelectOption>({
  children,
  disabled,
  ...props
}: SelectTriggerProps<T>) => {
  const {
    multiple,
    selectedOptions,
    setIsVisible,
    onSelect,
    options,
    isClearable,
    selectedOptionsWithValuesOnly,
    hasFooter,
    hasSearch,
    setSearchInputValue,
  } = useContext(SelectContext);

  const { handleKeyDown, removeFocusClass } = useKeyNavigation();

  const handleClear = (evt: React.MouseEvent | React.KeyboardEvent) => {
    evt.stopPropagation();

    const fixedOptions = getFixedOptions(options, selectedOptions);
    onSelect(fixedOptions);
    setSearchInputValue('');
    setIsVisible(false);
  };

  const handleRemove = (option: T) => {
    onSelect(
      getSelectedValues({
        option,
        selectedOptions,
        selectedOptionsWithValuesOnly: selectedOptionsWithValuesOnly || [],
        multiple,
      }),
    );
  };

  useEffect(() => {
    if (disabled) {
      setIsVisible(false);
    }
  }, [disabled, setIsVisible]);

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      switch (e.key) {
        case 'Tab':
          if (!hasSearch || e.shiftKey) {
            removeFocusClass();
          }

          if ((!hasFooter && !hasSearch) || e.shiftKey) {
            handleKeyDown(e);
          }
          break;

        default:
          handleKeyDown(e);
          break;
      }
    },
    [handleKeyDown, hasFooter, hasSearch, removeFocusClass],
  );

  return (
    <Popover.Trigger
      multiple={multiple}
      {...(props as PopoverTriggerProps<T>)}
      disabled={disabled}
      selectedOptions={selectedOptions as T[]}
      setListVisibility={setIsVisible}
      onClear={isClearable ? handleClear : undefined}
      onRemove={multiple ? handleRemove : undefined}
      onKeyDown={handleTriggerKeyDown}
    >
      {children}
    </Popover.Trigger>
  );
};

SelectTrigger.displayName = 'Trigger';

export default SelectTrigger;
